import { Component, EventEmitter, Inject, Input, OnInit, Output, DOCUMENT } from '@angular/core';

import { ApContentService } from '@ap/content';
import { ApPersistenceService } from '@ap/persistence';
import { PendingToken, SignCredentials, SignToken } from '@ap/sign';
import { ApThemeService } from '@ap/theme';
import { ApModalService, ModalOptions, ModalSize } from '@ap/modal';

import { NO_AVAILABLE_TOKENS_MODAL } from '../../../mijn-login/login/login.wrapper.constants';
import { MijnLoginAnalyticsService } from '../../../mijn-login/analytics/mijn-login-analytics.service';

import { NoAvailableTokensModalComponent } from './no-available-tokens-modal/no-available-tokens-modal.component';
import content from './sign.content.json';
import apSignContent from './ap-sign/ap-sign.content.json';

/**
 * @ngdoc component
 * @name SignComponent
 * @module SignModule
 */
@Component({
    selector: 'klant-login-sign',
    templateUrl: './sign.component.html',
    standalone: false
})
export class SignComponent implements OnInit {
    @Input()
    public actionId: string;

    @Input()
    public additionalTokens: SignToken[] = [];

    @Input()
    public preferredToken?: SignToken;

    @Input()
    public ariaLabelsForNotifications: string[];

    @Output()
    public lostBrowserCredentials = new EventEmitter<string>();

    @Output()
    public updatePassword = new EventEmitter<SignCredentials>();

    @Output()
    public updateUsernamePassword = new EventEmitter<SignCredentials>();

    @Output()
    public refreshActionId = new EventEmitter<void>();

    @Output()
    public pendingToken = new EventEmitter<PendingToken>();

    @Output()
    public tokenChanged = new EventEmitter<SignToken>();

    @Output()
    public completed = new EventEmitter<void>();

    public readonly saveTokenIdKey: string = 'klant-login';

    public hasBrowserRegister = true;

    public constructor(
        private readonly contentService: ApContentService,
        @Inject(DOCUMENT) private readonly document: HTMLDocument,
        private readonly persistenceService: ApPersistenceService,
        private readonly themeService: ApThemeService,
        private readonly mijnLoginAnalyticsService: MijnLoginAnalyticsService,
        private readonly modalService: ApModalService
    ) {}

    public ngOnInit(): void {
        this.contentService.addContentItems(apSignContent);
        this.contentService.addContentItems(content);
        if (this.themeService.getTheme() === 'blg') {
            this.hasBrowserRegister = false;
        }
    }

    public getInitToken(): SignToken {
        if (this.preferredToken) {
            return this.preferredToken;
        }
        return this.persistenceService.getLocal('ap-sign', 'token');
    }

    public onComplete(): void {
        this.completed.emit();
    }

    public onPendingToken(pendingToken: PendingToken): void {
        this.pendingToken.emit(pendingToken);
    }

    public onSignError(error: string) {
        if (error === 'no_available_tokens') {
            const modalOptions: ModalOptions<void> = new ModalOptions();
            modalOptions.title = this.contentService.get('mijn.login.login.wrapper.title');
            modalOptions.showHeader = true;
            modalOptions.cancelable = false;
            modalOptions.fullscreen = false;
            modalOptions.size = ModalSize.MEDIUM;

            this.modalService.open(NoAvailableTokensModalComponent, modalOptions);
            this.mijnLoginAnalyticsService.pushClickEvent(NO_AVAILABLE_TOKENS_MODAL, NO_AVAILABLE_TOKENS_MODAL);
        }
    }

    public onTokenChanged(signToken: SignToken): void {
        this.setPreferredToken(signToken);
        this.tokenChanged.emit(signToken);

        setTimeout(
            () => {
                this.setFocusOnActiveInput(signToken);
            },
            signToken === SignToken.BROWSER ? 1000 : 0
        );
    }

    public onLostBrowserCredentials(keychainId: string): void {
        this.lostBrowserCredentials.emit(keychainId);
    }

    public onUpdatePassword(signCredentials: SignCredentials): void {
        this.updatePassword.emit(signCredentials);
    }

    public onUpdateUsernamePassword(signCredentials: SignCredentials): void {
        this.updateUsernamePassword.emit(signCredentials);
    }

    public onRetry(): void {
        this.refreshActionId.emit();
    }

    public hasAdditionalTokens(): boolean {
        return this.additionalTokens?.length > 0;
    }

    private setPreferredToken(signToken: SignToken): void {
        if (signToken != null) {
            if (signToken !== SignToken.PHONE_NUMBER) {
                this.persistenceService.setLocal('ap-sign', 'token', signToken);
            }
            this.preferredToken = signToken;
        }
    }

    private setFocusOnActiveInput(signToken: SignToken) {
        let inputField: HTMLElement = null;

        switch (signToken) {
            case SignToken.DIGIPAS:
                inputField = this.document.querySelector('ap-sign ap-input[data-bb="tokenIdInput"] input[data-testid="input"]');
                break;
            case SignToken.DIGICODE:
            case SignToken.PROSPECT_DIGICODE:
                inputField = this.document.querySelector('ap-sign ap-input[data-bb="username"] input[data-testid="input"]');
                break;
            case SignToken.BROWSER:
                inputField = this.document.querySelector('ap-sign ap-pincode-input input');
                break;
        }
        if (inputField != null) {
            inputField.focus();
        }
    }
}
